import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement } from 'react';
import QRCode from 'qrcode';
import { ApiError } from '../shared/api/HttpClient';
import { connectionQualityLabel, itemTypeLabel, localizedProblem, stationModeLabel } from '../shared/i18n';
import { JanusLiveMediaAdapter } from '../shared/media/JanusLiveMediaAdapter';
import type { LiveMediaState } from '../shared/media/LiveMediaAdapter';
import type { StationSummary, StudioSnapshot } from '../studio/types';
import { MobileDjApi, type GuestInvitation, type LiveGuest, type OwnedLiveSession } from './MobileDjApi';
import { canUseLiveTransport, mediaSummary, secondsUntilLeaseLoss, type MobileLivePhase } from './mobileModel';
import './mobile.css';

const EMPTY_MEDIA: LiveMediaState = { connection: 'closed', quality: 'POOR', microphoneLevel: 0, latencyMs: null, jitterMs: null, packetLossPercent: null, monitorStream: null };

export function MobileDj(): ReactElement {
  const api = useMemo(() => new MobileDjApi(), []);
  const media = useMemo(() => new JanusLiveMediaAdapter(), []);
  const [stations, setStations] = useState<StationSummary[]>([]);
  const [stationId, setStationId] = useState('');
  const [snapshot, setSnapshot] = useState<StudioSnapshot | null>(null);
  const [session, setSession] = useState<OwnedLiveSession | null>(null);
  const [phase, setPhase] = useState<MobileLivePhase>('DISCONNECTED');
  const [mediaState, setMediaState] = useState<LiveMediaState>(EMPTY_MEDIA);
  const [lastHeartbeatAt, setLastHeartbeatAt] = useState<number | null>(null);
  const [heartbeatFailed, setHeartbeatFailed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hiddenWarning, setHiddenWarning] = useState(false);
  const [remoteActive, setRemoteActive] = useState(false);
  const [talking, setTalking] = useState(false);
  const [cueTalking, setCueTalking] = useState(false);
  const [guests, setGuests] = useState<LiveGuest[]>([]);
  const [guestName, setGuestName] = useState('');
  const [guestProgram, setGuestProgram] = useState('');
  const [invitation, setInvitation] = useState<GuestInvitation | null>(null);
  const [invitationQr, setInvitationQr] = useState<string | null>(null);
  const sessionRef = useRef<OwnedLiveSession | null>(null);
  const mediaRef = useRef(mediaState);
  const wakeLock = useRef<{ release(): Promise<void> } | null>(null);

  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { mediaRef.current = mediaState; }, [mediaState]);
  useEffect(() => media.subscribe(setMediaState), [media]);
  useEffect(() => () => { void media.disconnect(); void wakeLock.current?.release(); }, [media]);

  const loadStation = useCallback(async (id: string): Promise<void> => {
    const [nextSnapshot, current] = await Promise.all([api.snapshot(id), api.current(id)]);
    setSnapshot(nextSnapshot);
    setRemoteActive(current.session !== null);
  }, [api]);

  const load = useCallback(async (): Promise<void> => {
    try {
      const items = await api.stations();
      setStations(items);
      const requested = new URLSearchParams(window.location.search).get('station');
      const initial = items.find((item) => item.id === requested)?.id ?? items[0]?.id ?? '';
      setStationId(initial);
      if (initial !== '') await loadStation(initial);
      setAuthRequired(false);
    } catch (reason: unknown) {
      if (reason instanceof ApiError && reason.problem.status === 401) setAuthRequired(true);
      else setError(problem(reason));
    }
  }, [api, loadStation]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (stationId === '') return;
    const timer = window.setInterval(() => { void loadStation(stationId).catch(() => undefined); }, 2_000);
    return () => window.clearInterval(timer);
  }, [loadStation, stationId]);
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 250); return () => window.clearInterval(timer); }, []);
  useEffect(() => {
    if (session === null || stationId === '') { setGuests([]); return; }
    let active = true;
    const refresh = async (): Promise<void> => {
      try { const items = await api.guests(stationId, session.id, session.leaseToken); if (active) setGuests(items); } catch { /* lease heartbeat owns failure state */ }
    };
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, 2_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [api, session?.id, session?.leaseToken, stationId]);

  const acquireWakeLock = useCallback(async (): Promise<void> => {
    const source = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
    if (source.wakeLock === undefined || document.visibilityState !== 'visible') return;
    try { wakeLock.current = await source.wakeLock.request('screen'); } catch { setHiddenWarning(true); }
  }, []);

  const recover = useCallback(async (): Promise<void> => {
    const owned = sessionRef.current;
    if (owned === null || stationId === '' || !navigator.onLine) return;
    setPhase('RECOVERING');
    try {
      await media.disconnect();
      const credential = await api.reconnect(stationId, owned.id, owned.leaseToken);
      await media.connect(credential);
      setSession({ ...owned, credential });
      setPhase(owned.state === 'LIVE' ? 'LIVE' : 'READY');
      setHeartbeatFailed(false);
      setNotice('З’єднання з ефірною кімнатою відновлено.');
      if (owned.state === 'LIVE') await acquireWakeLock();
    } catch (reason: unknown) {
      setHeartbeatFailed(true);
      setError(problem(reason));
    }
  }, [acquireWakeLock, api, media, stationId]);

  useEffect(() => {
    if (session === null) return;
    const remaining = Date.parse(session.credential.expiresAt) - Date.now();
    const timer = window.setTimeout(() => { void recover(); }, Math.max(1_000, remaining - 60_000));
    return () => window.clearTimeout(timer);
  }, [recover, session]);

  useEffect(() => {
    function visibility(): void {
      const live = sessionRef.current?.state === 'LIVE';
      setHiddenWarning(live && document.visibilityState !== 'visible');
      if (live && document.visibilityState === 'visible') void recover();
    }
    function online(): void { void recover(); }
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('online', online);
    return () => { document.removeEventListener('visibilitychange', visibility); window.removeEventListener('online', online); };
  }, [recover]);

  useEffect(() => {
    if (session === null || stationId === '') return;
    let active = true;
    const beat = async (): Promise<void> => {
      try {
        const result = await api.heartbeat(stationId, session.id, session.leaseToken, mediaRef.current);
        if (!active) return;
        setLastHeartbeatAt(Date.now());
        setHeartbeatFailed(false);
        if (result.state !== session.state) setSession({ ...session, state: result.state });
      } catch (reason: unknown) {
        if (!active) return;
        setHeartbeatFailed(true);
        if (reason instanceof ApiError && reason.problem.code === 'live_lease_invalid') {
          setSession(null); setPhase('DISCONNECTED'); setTalking(false); void media.disconnect();
        }
      }
    };
    void beat();
    const timer = window.setInterval(() => { void beat(); }, 3_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [api, media, session, stationId]);

  async function connect(): Promise<void> {
    if (snapshot === null || stationId === '' || pending !== null) return;
    setPending('CONNECT'); setError(null); setNotice(null); setPhase('CONNECTING');
    let created: OwnedLiveSession | null = null;
    try {
      created = await api.start(stationId, snapshot.runtime.revision);
      setSession(created);
      await media.connect(created.credential);
      setPhase('READY'); setRemoteActive(true); setLastHeartbeatAt(Date.now());
      setNotice('Мікрофон підключено. Перевірте монітор і натисніть «Взяти керування».');
    } catch (reason: unknown) {
      if (created !== null) await api.returnAuto(stationId, created.id, created.leaseToken).catch(() => undefined);
      setSession(null); setPhase('DISCONNECTED'); setError(problem(reason));
      await media.disconnect();
    } finally { setPending(null); }
  }

  async function takeControl(): Promise<void> {
    if (session === null || snapshot === null || pending !== null) return;
    setPending('TAKE'); setError(null);
    try {
      const result = await api.take(stationId, session.id, session.leaseToken, snapshot.runtime.revision);
      const live = { ...session, state: result.state, effectiveMode: 'LIVE' as const };
      setSession(live); setPhase('LIVE'); setLastHeartbeatAt(Date.now());
      await acquireWakeLock();
      await loadStation(stationId);
    } catch (reason: unknown) { setError(problem(reason)); }
    finally { setPending(null); }
  }

  async function returnAuto(): Promise<void> {
    if (session === null || pending !== null) return;
    setPending('RETURN'); setError(null);
    try {
      setTalking(false); setCueTalking(false); await media.setMicrophoneEnabled(false).catch(() => undefined);
      await api.returnAuto(stationId, session.id, session.leaseToken);
      await media.disconnect(); await wakeLock.current?.release(); wakeLock.current = null;
      setSession(null); setPhase('DISCONNECTED'); setRemoteActive(false); setHeartbeatFailed(false);
      setNotice('Станцію безпечно повернуто в автоматичний ефір.'); await loadStation(stationId);
    } catch (reason: unknown) { setError(problem(reason)); }
    finally { setPending(null); }
  }

  async function talk(enabled: boolean): Promise<void> {
    if (!canUseLiveTransport(phase, mediaState.connection)) return;
    try { await media.setMicrophoneEnabled(enabled); setTalking(enabled); }
    catch (reason: unknown) { setTalking(false); setError(problem(reason)); }
  }

  async function cueTalk(enabled: boolean): Promise<void> {
    if (!canUseLiveTransport(phase, mediaState.connection)) return;
    try {
      if (enabled) { await media.setRoutingGroup('backstage'); await media.setMicrophoneEnabled(true); setCueTalking(true); }
      else { await media.setMicrophoneEnabled(false); await media.setRoutingGroup('onair'); setCueTalking(false); }
    } catch (reason: unknown) {
      await media.setMicrophoneEnabled(false).catch(() => undefined);
      await media.setRoutingGroup('onair').catch(() => undefined);
      setCueTalking(false); setError(problem(reason));
    }
  }

  async function inviteGuest(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (session === null || guestName.trim() === '' || pending !== null) return;
    setPending('INVITE'); setError(null);
    try {
      const from = new Date();
      const until = new Date(from.getTime() + 2 * 60 * 60 * 1_000);
      const created = await api.invite(stationId, session.id, session.leaseToken, guestName.trim(), guestProgram.trim() || null, from.toISOString(), until.toISOString());
      const absolute = new URL(created.invitationUrl, window.location.origin).toString();
      setInvitation({ ...created, invitationUrl: absolute });
      setInvitationQr(await QRCode.toDataURL(absolute, { width: 256, margin: 1, color: { dark: '#0b0d0f', light: '#ffffff' } }));
      setGuestName(''); setNotice('Одноразове запрошення готове. Надішліть посилання або QR гостю.');
    } catch (reason: unknown) { setError(problem(reason)); }
    finally { setPending(null); }
  }

  async function copyInvitation(): Promise<void> {
    if (invitation === null) return;
    try { await navigator.clipboard.writeText(invitation.invitationUrl); setNotice('Посилання скопійовано.'); }
    catch { setError('Не вдалося скопіювати посилання. Скопіюйте його вручну.'); }
  }

  async function guestAction(guest: LiveGuest, action: 'CUE' | 'ON_AIR' | 'MUTE' | 'REMOVE' | 'LEVEL', level?: number): Promise<void> {
    if (session === null || pending !== null) return;
    setPending(`${action}:${guest.id}`); setError(null);
    try {
      if (action === 'CUE') await api.routeGuest(stationId, session.id, session.leaseToken, guest.id, 'backstage');
      if (action === 'ON_AIR') await api.routeGuest(stationId, session.id, session.leaseToken, guest.id, 'onair');
      if (action === 'MUTE') await api.muteGuest(stationId, session.id, session.leaseToken, guest.id, guest.state !== 'MUTED');
      if (action === 'REMOVE') await api.removeGuest(stationId, session.id, session.leaseToken, guest.id);
      if (action === 'LEVEL' && level !== undefined) await api.levelGuest(stationId, session.id, session.leaseToken, guest.id, level);
      setGuests(await api.guests(stationId, session.id, session.leaseToken));
    } catch (reason: unknown) { setError(problem(reason)); }
    finally { setPending(null); }
  }

  async function transport(type: 'NEXT' | 'FADE_NOW' | 'CART_TRIGGER'): Promise<void> {
    if (session === null || snapshot === null || pending !== null || !canUseLiveTransport(phase, mediaState.connection)) return;
    const parameters = type === 'CART_TRIGGER' ? { cartItemId: snapshot.cartWall.items[0]?.id ?? '' } : {};
    if (type === 'CART_TRIGGER' && parameters.cartItemId === '') return;
    setPending(type); setError(null);
    try {
      const receipt = await api.command(stationId, session.id, type, snapshot.runtime.revision, parameters);
      if (receipt.state !== 'SUCCEEDED') throw new Error('Radio Engine не підтвердив команду.');
      await loadStation(stationId);
    } catch (reason: unknown) { setError(problem(reason)); }
    finally { setPending(null); }
  }

  async function submitLogin(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault(); setPending('LOGIN'); setError(null);
    try { await api.login(email, password); setPassword(''); await load(); }
    catch (reason: unknown) { setError(problem(reason)); }
    finally { setPending(null); }
  }

  const leaseCountdown = heartbeatFailed && session !== null ? secondsUntilLeaseLoss(lastHeartbeatAt, session.leaseTtlSeconds, now) : null;
  const connected = mediaState.connection === 'connected';
  const liveControls = canUseLiveTransport(phase, mediaState.connection);

  if (authRequired) return (
    <main className="mobile-dj mobile-login"><form onSubmit={(event) => void submitLogin(event)}><p className="mobile-kicker">ЛОКАЛЬНА ПАНЕЛЬ КЕРУВАННЯ</p><h1>Мобільний пульт</h1><p>Увійдіть локальним обліковим записом цієї інсталяції.</p><label htmlFor="dj-email">Електронна пошта</label><input id="dj-email" type="email" required autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} /><label htmlFor="dj-password">Пароль</label><input id="dj-password" type="password" required autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /><button type="submit" disabled={pending !== null}>УВІЙТИ</button>{error !== null && <p className="mobile-error" role="alert">{error}</p>}</form></main>
  );

  return (
    <main className={`mobile-dj phase-${phase.toLowerCase()}`}>
      <header className="mobile-head"><a href="/dj" aria-label="SweetLana — пульт ведучого">SL <span>ЕФІР</span></a><div className="mobile-station"><label htmlFor="mobile-station">СТАНЦІЯ</label><select id="mobile-station" value={stationId} disabled={session !== null} onChange={(event) => { setStationId(event.target.value); void loadStation(event.target.value); }}>{stations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div><div className={`mobile-status ${connected ? 'connected' : ''}`}><i /><span>{phaseLabel(phase)}</span></div></header>

      {(hiddenWarning || (phase === 'LIVE' && !document.hasFocus())) && <div className="mobile-warning" role="alert">Під час прямого ефіру залиште застосунок відкритим та не блокуйте екран</div>}
      {heartbeatFailed && <div className="mobile-danger" role="alert"><strong>Втрата зв’язку</strong><span>{leaseCountdown === null ? 'Перепідключення…' : `Автоефір через ${leaseCountdown} с`}</span></div>}
      {error !== null && <div className="mobile-error" role="alert">{error}</div>}
      {notice !== null && <div className="mobile-notice" role="status">{notice}</div>}

      <section className="mobile-now" aria-label="Зараз в ефірі"><p className="mobile-kicker">ЗАРАЗ / {itemTypeLabel(snapshot?.current?.itemType ?? 'PROGRAMME')}</p><h1>{snapshot?.current?.title ?? 'Очікування програми'}</h1><p>{snapshot?.current?.artist ?? stations.find((item) => item.id === stationId)?.name ?? 'SweetLana'}</p><div className="mobile-next"><span>ДАЛІ</span><strong>{snapshot?.next?.title ?? 'План готується'}</strong></div></section>

      <section className="mobile-console" aria-label="Керування прямим ефіром">
        <div className="mobile-meter"><span style={{ '--level': `${Math.round(mediaState.microphoneLevel * 100)}%` } as React.CSSProperties} /><small>МІКРОФОН</small><strong>{mediaSummary(mediaState)}</strong></div>
        {phase === 'DISCONNECTED' ? <button className="mobile-connect" type="button" disabled={pending !== null || remoteActive || snapshot?.runtime.engineState !== 'ON_AIR'} onClick={() => void connect()}>{pending === 'CONNECT' ? 'ПІДКЛЮЧАЄМО…' : remoteActive ? 'СЕСІЯ ВЖЕ АКТИВНА' : 'ПІДКЛЮЧИТИ МІКРОФОН'}</button> : null}
        {phase === 'CONNECTING' || phase === 'RECOVERING' ? <div className="mobile-connecting"><i /><strong>{phase === 'RECOVERING' ? 'Відновлення каналу' : 'Узгодження захищеного аудіоканалу'}</strong></div> : null}
        {phase === 'READY' ? <button className="mobile-take" type="button" disabled={pending !== null || !connected} onClick={() => void takeControl()}>ВЗЯТИ КЕРУВАННЯ<span>Автоефір працює до підтвердження</span></button> : null}
        {phase === 'LIVE' ? <button className={`mobile-talk ${talking ? 'active' : ''}`} type="button" disabled={!liveControls || cueTalking} aria-pressed={talking} onPointerDown={() => void talk(true)} onPointerUp={() => void talk(false)} onPointerCancel={() => void talk(false)} onPointerLeave={() => { if (talking) void talk(false); }}><span>{talking ? 'МІКРОФОН В ЕФІРІ' : 'УТРИМУЙТЕ, ЩОБ ГОВОРИТИ'}</span><small>{talking ? 'Вас чують слухачі' : 'Відпустіть, щоб вимкнути мікрофон'}</small></button> : null}
      </section>

      <section className="mobile-transport" aria-label="Керування відтворенням"><button type="button" disabled={!liveControls || pending !== null} onClick={() => void transport('NEXT')}><span>↦</span>ДАЛІ</button><button type="button" disabled={!liveControls || pending !== null} onClick={() => void transport('FADE_NOW')}><span>⌁</span>ЗГАСИТИ</button><button type="button" disabled={!liveControls || pending !== null || (snapshot?.cartWall.items.length ?? 0) === 0} onClick={() => void transport('CART_TRIGGER')}><span>●</span>ДЖИНГЛ</button></section>

      {session !== null && <section className="mobile-guests" aria-label="Керування гостями">
        <div className="mobile-guests-head"><div><p className="mobile-kicker">ДИСТАНЦІЙНА КІМНАТА</p><h2>Гості <span>{guests.filter((guest) => guest.state !== 'DISCONNECTED').length}</span></h2></div>{guests.some((guest) => guest.routingGroup === 'backstage' && guest.state !== 'DISCONNECTED') && <button className={cueTalking ? 'active' : ''} type="button" disabled={!liveControls || talking} onPointerDown={() => void cueTalk(true)} onPointerUp={() => void cueTalk(false)} onPointerCancel={() => void cueTalk(false)} onPointerLeave={() => { if (cueTalking) void cueTalk(false); }}>{cueTalking ? 'ЗВ’ЯЗОК УВІМКНЕНО' : 'УТРИМУЙТЕ ДЛЯ ЗВ’ЯЗКУ'}</button>}</div>
        <form className="mobile-invite" onSubmit={(event) => void inviteGuest(event)}><input aria-label="Ім’я гостя" placeholder="Ім’я гостя" maxLength={120} required value={guestName} onChange={(event) => setGuestName(event.target.value)} /><input aria-label="Назва програми" placeholder="Програма або сесія (необов’язково)" maxLength={160} value={guestProgram} onChange={(event) => setGuestProgram(event.target.value)} /><button type="submit" disabled={pending !== null}>СТВОРИТИ ЗАПРОШЕННЯ</button></form>
        {invitation !== null && <div className="mobile-invitation"><div>{invitationQr !== null && <img src={invitationQr} alt="QR-код одноразового гостьового запрошення" />}<div><strong>{invitation.guestName}</strong><small>Діє до {new Date(invitation.validUntil).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}. Посилання показується один раз.</small></div></div><input aria-label="Посилання-запрошення" readOnly value={invitation.invitationUrl} /><button type="button" onClick={() => void copyInvitation()}>КОПІЮВАТИ ПОСИЛАННЯ</button></div>}
        <div className="mobile-guest-list">{guests.length === 0 ? <p className="mobile-guest-empty">Створіть запрошення. Після перевірки гість з’явиться тут за лаштунками.</p> : guests.map((guest) => <article key={guest.id} className={`mobile-guest guest-${guest.state.toLowerCase().replace('_', '-')}`}><div className="mobile-guest-name"><i /><div><strong>{guest.displayName}</strong><small>{guestStateLabel(guest.state)} · {guest.quality === null ? 'очікування' : connectionQualityLabel(guest.quality)}{guest.latencyMs === null ? '' : ` · ${guest.latencyMs} мс`}</small></div></div><div className="mobile-guest-actions"><button type="button" disabled={guest.state === 'DISCONNECTED' || pending !== null} onClick={() => void guestAction(guest, 'CUE')}>ЗА ЛАШТУНКИ</button><button className="onair" type="button" disabled={phase !== 'LIVE' || guest.state === 'DISCONNECTED' || pending !== null} onClick={() => void guestAction(guest, 'ON_AIR')}>В ЕФІР</button><button type="button" disabled={guest.state === 'DISCONNECTED' || pending !== null} onClick={() => void guestAction(guest, 'MUTE')}>{guest.state === 'MUTED' ? 'УВІМКНУТИ' : 'ВИМКНУТИ'}</button><button className="remove" type="button" disabled={guest.state === 'DISCONNECTED' || pending !== null} onClick={() => void guestAction(guest, 'REMOVE')}>ВИДАЛИТИ</button></div><label>РІВЕНЬ <input type="range" min="0" max="100" defaultValue={guest.level} disabled={guest.state === 'DISCONNECTED' || pending !== null} onPointerUp={(event) => void guestAction(guest, 'LEVEL', Number(event.currentTarget.value))} /></label></article>)}</div>
      </section>}

      <footer className="mobile-foot"><div><small>РЕЖИМ ПРОГРАМИ</small><strong>{snapshot === null ? '—' : stationModeLabel(snapshot.runtime.effectiveMode)} · версія {snapshot?.runtime.revision ?? '—'}</strong></div>{session !== null && <button type="button" disabled={pending !== null} onClick={() => void returnAuto()}>ПОВЕРНУТИ АВТОЕФІР</button>}</footer>
    </main>
  );
}

function phaseLabel(phase: MobileLivePhase): string {
  const labels: Record<MobileLivePhase, string> = {
    DISCONNECTED: 'відключено', CONNECTING: 'підключення', READY: 'готово',
    LIVE: 'прямий ефір', RECOVERING: 'відновлення',
  };
  return labels[phase];
}

function guestStateLabel(state: LiveGuest['state']): string {
  const labels: Record<LiveGuest['state'], string> = {
    CONNECTED: 'підключено', BACKSTAGE: 'за лаштунками', ON_AIR: 'в ефірі',
    MUTED: 'мікрофон вимкнено', DISCONNECTED: 'відключено',
  };
  return labels[state];
}

function problem(reason: unknown): string {
  if (reason instanceof ApiError) return localizedProblem(reason.problem);
  if (reason instanceof DOMException && reason.name === 'NotAllowedError') return 'Дозвольте доступ до мікрофона в налаштуваннях браузера.';
  return reason instanceof Error ? reason.message : 'Не вдалося завершити операцію.';
}
